import {FC} from 'preact/compat'
import {useState} from 'react'
import useUser from '../../../hooks/User'
import {IconButton, Menu, MenuItem} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import {useIntl} from 'react-intl'
import {CellContext, ColumnDef} from '@tanstack/react-table'
import {MasterDataTableTanstack} from '../../../components/DataTable/MasterDataTableTanstack'
import useDynamicTab from '../../../hooks/useDynamicTab'
import {ExpandableElement} from '../../../components/General/ExpandableElement'
import useModalEffect from '../../../hooks/useModalEffect'
import AddVariantModal from '../../../components/VariantPrice/AddVariantModal'
import UpdateVariantModal from '../../../components/VariantPrice/UpdateVariantModal'
import {IPriceVariant} from '../../../interfaces/PriceVariant'
import {DeleteVariantModal} from '../../../components/VariantPrice/DeleteVariantModal'

const MasterVariantPrice: FC = () => {
  const user = useUser()

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false)
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)
  const [selectedVariant, setSelectedVariant] = useState<IPriceVariant>()
  const intl = useIntl()

  const handleClick = (menuId: string) => {
    setOpenMenuId(menuId)
  }
  const handleClose = () => {
    setOpenMenuId(null)
  }

  const columns: ColumnDef<any>[] = [
    {
      id: 'action',
      meta: {
        // size: 12,
        // maxSize: 12,
        styleHeader: {
          alignContent: 'center',
          textAlign: 'center',
        },
        styleBody: {
          alignContent: 'center',
          textAlign: 'center',
          width: '30px',
          overflow: 'auto',
        },
        isPinnedLeft: true,
      },
      header: intl.formatMessage({id: 'MASTERS.DATATABLE.COLUMNS.ACTION'}),
      cell: (props: CellContext<IPriceVariant, unknown>) => {
        const row = props.row.original
        return (
          <>
            {user.data.is_superadmin && (
              <>
                <IconButton
                  aria-label={`more-${row.id}`}
                  id={`menu-anchor-${row.id}`}
                  onClick={() => handleClick(row.id)}
                >
                  <MoreVertIcon />
                </IconButton>

                <Menu
                  id={`menu-${row.id}`}
                  anchorEl={document.getElementById(`menu-anchor-${row.id}`)}
                  open={openMenuId === row.id}
                  onClose={handleClose}
                  disableScrollLock={true}
                >
                  {user.data.is_superadmin && (
                    <MenuItem
                      onClick={() => {
                        handleClose()
                        setShowUpdateModal(true)
                        setSelectedVariant(row)
                      }}
                    >
                      <i className='fas fa-edit fs-4 me-4'></i>
                      <span>{intl.formatMessage({id: 'FORM.ACTION.UPDATE'})}</span>
                    </MenuItem>
                  )}
                  {user.data.is_superadmin && (
                    <MenuItem
                      onClick={() => {
                        handleClose()
                        setShowDeleteModal(true)
                        setSelectedVariant(row)
                      }}
                    >
                      <i className='fas fa-trash fs-3 me-5'></i>
                      <span>{intl.formatMessage({id: 'FORM.ACTION.DELETE'})}</span>
                    </MenuItem>
                  )}
                </Menu>
              </>
            )}
          </>
        )
      },
    },
    {
      header: intl.formatMessage({id: 'MASTERS.DATATABLE.COLUMNS.NAME'}),
      accessorKey: 'variantPriceName',
    },
    {
      accessorKey: 'note',
      header: 'Note',
      cell: ({row}) => {
        const note = row.original.note
        return note ? (
          <ExpandableElement
            content={<div style={{whiteSpace: 'pre-wrap'}}>{note}</div>}
            maxHeight={100}
          />
        ) : (
          '-'
        )
      },
    },
  ]

  const actions = [<AddVariantModal />]

  useModalEffect({
    showModal: showUpdateModal,
    setShowModal: setShowUpdateModal,
    modalId: `update-priceVariant-modal-${selectedVariant?.id}`,
  })
  useModalEffect({
    showModal: showDeleteModal,
    setShowModal: setShowDeleteModal,
    modalId: `delete-priceVariant-modal-${selectedVariant?.id}`,
  })

  useDynamicTab({title: `${intl.formatMessage({id: 'PRICE_VARIANT'})} | CRM WooBlazz`})

  return (
    <>
      <ol className='breadcrumb text-muted fs-6 fw-bold mb-5'>
        <li className='breadcrumb-item text-dark'>{intl.formatMessage({id: 'PRICE_VARIANT'})}</li>
      </ol>

      <MasterDataTableTanstack
        tableKey='priceVariant'
        tableColumns={columns}
        actions={actions}
        apiURL='variant-prices'
        autoReload={true}
      />
      {showUpdateModal && <UpdateVariantModal priceVariant={selectedVariant!} />}
      {showDeleteModal && <DeleteVariantModal priceVariant={selectedVariant!} />}
    </>
  )
}

export {MasterVariantPrice}
